/**
 * Invite Code Service
 *
 * Handles invite code validation and redemption API calls.
 * Authenticated users can validate an invite code to preview the target
 * organization, then redeem it to join that organization.
 *
 * Endpoints are on tenant-api:
 *   POST /invite-codes/validate  → preview code (tenant name, roles)
 *   POST /invite-codes/redeem    → join the organization
 *
 * Requirements: 3.1
 */

import { TENANT_API_URL } from '../config/api.config';
import { tokenService } from './token.service';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Response shape from POST /invite-codes/validate */
export interface ValidateCodeResponse {
  valid: boolean;
  tenantName?: string;
  roles?: string[];
}

/** Successful response shape from POST /invite-codes/redeem */
export interface RedeemCodeResponse {
  success: boolean;
  tenantId?: string;
  tenantName?: string;
  roles?: string[];
}

/** Error thrown when the user is already a member of the target tenant (409) */
export class MembershipAlreadyExistsError extends Error {
  constructor(message = 'You are already a member of this organization.') {
    super(message);
    this.name = 'MembershipAlreadyExistsError';
  }
}

/** Error thrown when rate limit is exceeded (429) */
export class InviteCodeRateLimitError extends Error {
  public retryAfter: number;

  constructor(retryAfter: number, message = 'Too many attempts. Please try again later.') {
    super(message);
    this.name = 'InviteCodeRateLimitError';
    this.retryAfter = retryAfter;
  }
}

// ─── Service ────────────────────────────────────────────────────────────────

/**
 * Invite Code Service Class
 *
 * Thin API client for invite-code validation and redemption endpoints.
 */
export class InviteCodeService {
  /**
   * Validate an invite code without redeeming it.
   *
   * Returns the target tenant name and roles so the UI can display
   * a confirmation step before the user commits to joining.
   *
   * @param code - The 8-character invite code
   * @returns Validation result with tenant info when valid
   */
  async validateCode(code: string): Promise<ValidateCodeResponse> {
    const accessToken = tokenService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available. Please log in again.');
    }

    const response = await fetch(`${TENANT_API_URL}/invite-codes/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ code: code.trim().toLowerCase() }),
    });

    if (response.status === 429) {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '60', 10);
      throw new InviteCodeRateLimitError(retryAfter);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData?.error?.message ||
        errorData?.message ||
        (typeof errorData?.error === 'string' ? errorData.error : null) ||
        `Code validation failed: ${response.status}`;
      throw new Error(message);
    }

    const data: ValidateCodeResponse = await response.json();
    return data;
  }

  /**
   * Redeem an invite code to join the associated organization.
   *
   * On success the user gains membership in the target tenant with
   * the roles specified at code creation time.
   *
   * @param code - The 8-character invite code
   * @returns Redemption result with tenant info on success
   * @throws MembershipAlreadyExistsError if user is already a member (409)
   * @throws InviteCodeRateLimitError if rate limit exceeded (429)
   */
  async redeemCode(code: string): Promise<RedeemCodeResponse> {
    const accessToken = tokenService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available. Please log in again.');
    }

    const response = await fetch(`${TENANT_API_URL}/invite-codes/redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ code: code.trim().toLowerCase() }),
    });

    if (response.status === 409) {
      throw new MembershipAlreadyExistsError();
    }

    if (response.status === 429) {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '60', 10);
      throw new InviteCodeRateLimitError(retryAfter);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData?.error?.message ||
        errorData?.message ||
        (typeof errorData?.error === 'string' ? errorData.error : null) ||
        `Code redemption failed: ${response.status}`;
      throw new Error(message);
    }

    const data: RedeemCodeResponse = await response.json();
    return data;
  }
}

// Export singleton instance
export const inviteCodeService = new InviteCodeService();
